import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
  TASK_TIME_STATUS,
  TASK_TIME_MAX_HOURS,
  TASK_TIME_MIN_HOURS,
  TASK_TIME_STEP,
  TASK_TIME_RATIO,
  normalizeTaskTimeDepartment,
  normalizeTaskTimeStatus,
  taskTimeRequestHours,
} from '../utils/taskTimeConstants';
import {
  computeStrategyCallUsage,
  findStrategyCallDeliverableContext,
} from '../utils/accountProgramDeliverables';

const COLLECTION = 'taskTimeRequests';

export function roundToHalfHour(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error('Hours must be a positive number.');
  }
  const rounded = Math.round(n / TASK_TIME_STEP) * TASK_TIME_STEP;
  if (rounded < TASK_TIME_MIN_HOURS || rounded > TASK_TIME_MAX_HOURS) {
    throw new Error(`Hours must be between ${TASK_TIME_MIN_HOURS} and ${TASK_TIME_MAX_HOURS} in 30-minute increments.`);
  }
  return rounded;
}

export function formatTaskTimeHours(hours) {
  const n = Number(hours);
  if (!Number.isFinite(n) || n === 0) return '0h';
  return `${n}h`;
}

/** Implementation Hours pool = strategy call hours (× ratio), stepped to 30 min (min one step when calls exist). */
export function computeAllowanceFromCallLimit(callLimit) {
  const n = Number(callLimit);
  if (!Number.isFinite(n) || n <= 0) return 0;
  const raw = n * TASK_TIME_RATIO;
  const stepped = Math.round(raw / TASK_TIME_STEP) * TASK_TIME_STEP;
  return Math.max(TASK_TIME_STEP, stepped);
}

async function callTaskTimeFn(name, payload) {
  try {
    const fn = httpsCallable(functions, name);
    const res = await fn(payload);
    return res.data;
  } catch (e) {
    // Firebase wraps HttpsError; surface the server message to the UI.
    throw new Error(e?.message || 'Task time action failed.');
  }
}

function parseAnchorDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export async function createTaskTimeRequest({
  accountId,
  department,
  title,
  description,
  requestedByName,
  hours: hoursInput,
  attachments,
}) {
  if (!accountId) throw new Error('Account ID is required.');
  const titleTrim = title?.trim();
  if (!titleTrim) throw new Error('Title is required.');
  // The one time amount on the request; validated to the 30-minute step.
  const hours = hoursInput != null && hoursInput !== '' ? roundToHalfHour(hoursInput) : undefined;

  // Already-uploaded Storage files: pass only the display fields through.
  const files = Array.isArray(attachments)
    ? attachments
        .filter((f) => f?.url)
        .map((f) => ({
          name: String(f.name || 'Attachment'),
          url: String(f.url),
          size: Number(f.size) || 0,
          type: String(f.type || ''),
        }))
    : undefined;

  return callTaskTimeFn('taskTimeCreate', {
    accountId,
    department: normalizeTaskTimeDepartment(department),
    title: titleTrim,
    description: description?.trim() || '',
    requestedByName: requestedByName?.trim() || undefined,
    hours,
    attachments: files && files.length > 0 ? files : undefined,
  });
}

export async function listTaskTimeRequests(accountId, { status, department } = {}) {
  if (!accountId) return [];

  const q = query(
    collection(db, COLLECTION),
    where('accountId', '==', accountId),
    orderBy('createdAt', 'desc')
  );

  const snap = await getDocs(q);
  // Legacy statuses/hour fields are folded into the current four-status,
  // single-hours shape here so no consumer has to know about them.
  let rows = snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      ...data,
      department: normalizeTaskTimeDepartment(data.department),
      status: normalizeTaskTimeStatus(data.status),
      hours: taskTimeRequestHours(data),
    };
  });

  if (department && department !== 'all') {
    const target = normalizeTaskTimeDepartment(department);
    rows = rows.filter((r) => r.department === target);
  }

  if (status && status !== 'all') {
    rows = rows.filter((r) => r.status === status);
  }

  return rows;
}

/** Internal-only: adjust the request's time amount (before or after approval). */
export async function setTaskTimeHours(requestId, { hours, crmNotes }) {
  roundToHalfHour(hours); // client-side input validation; server is authoritative
  return callTaskTimeFn('taskTimeSetHours', {
    requestId,
    hours: Number(hours),
    crmNotes: crmNotes?.trim() || '',
  });
}

/**
 * Approve or deny a request. Either the client (from the portal) or the team
 * (from the CRM) can respond; the callable enforces the pooled balance cap and
 * stamps the billing period server-side so the hours deduct from that period.
 */
export async function respondToTaskTimeRequest(requestId, { approved, respondedByName } = {}) {
  return callTaskTimeFn('taskTimeRespond', {
    requestId,
    approved: approved === true,
    respondedByName: respondedByName?.trim() || undefined,
  });
}

/**
 * CRM shortcut: set the hours and approve in one go (the client already agreed
 * verbally), so the time deducts from the current period without a round trip
 * through the portal.
 */
export async function setHoursAndApprove(requestId, { hours, crmNotes, respondedByName }) {
  await setTaskTimeHours(requestId, { hours, crmNotes });
  return respondToTaskTimeRequest(requestId, { approved: true, respondedByName });
}

export async function startTaskTimeRequest(requestId) {
  return callTaskTimeFn('taskTimeStart', { requestId });
}

export async function completeTaskTimeRequest(requestId) {
  return callTaskTimeFn('taskTimeComplete', { requestId });
}

export async function denyTaskTimeRequest(requestId, { crmNotes } = {}) {
  return callTaskTimeFn('taskTimeCancel', { requestId, crmNotes: crmNotes?.trim() || '' });
}

/** Internal-only: permanently remove a request row. */
export async function deleteTaskTimeRequest(requestId) {
  return callTaskTimeFn('taskTimeDelete', { requestId });
}

/**
 * Internal-only: rewrite the account's Implementation Hours deliverable logs
 * from its task time requests. Idempotent; used to backfill tasks approved
 * before approval started writing deliverable completions.
 */
export async function syncTaskTimeDeliverableLogs(accountId) {
  return callTaskTimeFn('taskTimeSyncDeliverableLogs', { accountId });
}

/** Live requests whose hours never made it onto the deliverable. */
export function findUnloggedTaskTimeRequests(requests = []) {
  return requests.filter(
    (r) =>
      DEDUCT_STATUSES.includes(normalizeTaskTimeStatus(r.status)) &&
      !r.deliverableLogId &&
      taskTimeRequestHours(r) != null
  );
}

// Hours come off the pool at approval and stay off through completion.
const DEDUCT_STATUSES = [
  TASK_TIME_STATUS.APPROVED,
  TASK_TIME_STATUS.IN_PROGRESS,
  TASK_TIME_STATUS.COMPLETED,
];

/**
 * True when approving the request already wrote a completion onto the
 * Implementation Hours deliverable. Those hours arrive through `usedCallHours`
 * (which sums every deliverable log), so counting the request again here would
 * double-deduct.
 */
function countsAgainstPoolDirectly(request) {
  return !request?.deliverableLogId;
}

function requestStatus(request) {
  return normalizeTaskTimeStatus(request?.status);
}

function requestInPeriod(request, { periodKey, periodStart, periodEnd }) {
  if (!periodKey) return true;
  if (request.periodKey) return request.periodKey === periodKey;
  // Legacy requests have no periodKey — attribute by approval date.
  const anchor = parseAnchorDate(
    request.clientRespondedAt || request.allottedAt || request.requestedAt || request.createdAt
  );
  if (!anchor) return false;
  if (periodStart && anchor < periodStart) return false;
  if (periodEnd && anchor >= periodEnd) return false;
  return true;
}

/**
 * Pooled Implementation Hours balance for the current billing period.
 * Strategy calls (1h each, `usedCallHours`) and task hours share the same pool;
 * a task's hours deduct the moment it is approved and stay deducted once
 * completed.
 */
export function computeTaskTimeBalance(requests, { allowanceHours, usedCallHours = 0, periodKey, periodStart, periodEnd } = {}) {
  const period = { periodKey, periodStart, periodEnd };
  const inPeriod = requests.filter((r) => requestInPeriod(r, period));

  const deductedHours = inPeriod
    .filter((r) => DEDUCT_STATUSES.includes(requestStatus(r)) && countsAgainstPoolDirectly(r))
    .reduce((sum, r) => sum + (taskTimeRequestHours(r) || 0), 0);

  const completedHours = inPeriod
    .filter((r) => requestStatus(r) === TASK_TIME_STATUS.COMPLETED)
    .reduce((sum, r) => sum + (taskTimeRequestHours(r) || 0), 0);

  const requestedCount = requests.filter(
    (r) => requestStatus(r) === TASK_TIME_STATUS.REQUESTED
  ).length;
  const approvedCount = requests.filter((r) =>
    [TASK_TIME_STATUS.APPROVED, TASK_TIME_STATUS.IN_PROGRESS].includes(requestStatus(r))
  ).length;
  const completedCount = requests.filter(
    (r) => requestStatus(r) === TASK_TIME_STATUS.COMPLETED
  ).length;

  const callHours = Number(usedCallHours) || 0;
  const remainingHours =
    allowanceHours != null
      ? Math.max(0, Math.round((allowanceHours - deductedHours - callHours) * 100) / 100)
      : null;

  return {
    deductedHours,
    usedCallHours: callHours,
    completedHours,
    remainingHours,
    requestedCount,
    approvedCount,
    completedCount,
    atLimit: allowanceHours != null && deductedHours + callHours >= allowanceHours - 1e-9,
  };
}

export function computeTaskTimeMetrics(requests) {
  const hoursUsed = requests
    .filter((r) => DEDUCT_STATUSES.includes(requestStatus(r)))
    .reduce((sum, r) => sum + (taskTimeRequestHours(r) || 0), 0);

  const pendingCount = requests.filter(
    (r) => requestStatus(r) === TASK_TIME_STATUS.REQUESTED
  ).length;

  const activeCount = requests.filter((r) =>
    [TASK_TIME_STATUS.APPROVED, TASK_TIME_STATUS.IN_PROGRESS].includes(requestStatus(r))
  ).length;

  return { hoursUsed, pendingCount, activeCount };
}

const PROGRAM_STATUS_MAP = {
  Lite: 'Lite',
  Plus: 'Plus',
  Pro: 'Pro',
  Hourly: 'Hourly',
  '5 Hours': '5 Hours',
  '10 Hours': '10 Hours',
  'A La Carte': 'A La Carte',
  Custom: 'Custom',
};

/**
 * CRM-side allowance lookup: loads the account + program deliverable context and
 * derives the pooled task time allowance from the strategy call limit.
 */
export async function fetchAccountTaskTimeAllowance(accountId) {
  const empty = {
    allowanceHours: null,
    callLimit: null,
    usedCallHours: 0,
    periodKey: null,
    periodStart: null,
    periodEnd: null,
    periodDisplay: null,
  };
  if (!accountId) return empty;

  const accSnap = await getDoc(doc(db, 'accounts', accountId));
  if (!accSnap.exists()) return empty;
  const account = { id: accSnap.id, ...accSnap.data() };

  const programStatus =
    typeof account.program === 'string' ? account.program : account.program?.status;
  if (!programStatus || programStatus === 'Not Started') {
    return { ...empty, allowanceHours: computeAllowanceFromCallLimit(1), callLimit: 1 };
  }

  try {
    const programId = PROGRAM_STATUS_MAP[programStatus] || programStatus;
    const [contractItemsSnap, alaCarteSnap] = await Promise.all([
      getDocs(collection(db, 'programs', programId, 'contractItems')),
      getDocs(collection(db, 'alaCarteItems')),
    ]);
    const contractItems = contractItemsSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const itemsData = alaCarteSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const context = findStrategyCallDeliverableContext(account, contractItems, itemsData, programStatus);
    const usage = computeStrategyCallUsage(account, null, context);
    if (!usage) {
      return { ...empty, allowanceHours: computeAllowanceFromCallLimit(1), callLimit: 1 };
    }
    return {
      allowanceHours: computeAllowanceFromCallLimit(usage.limit),
      callLimit: usage.limit,
      usedCallHours: Number(usage.used) || 0,
      periodKey: usage.billingPeriod?.periodKey || null,
      periodStart: usage.billingPeriod?.periodStart || null,
      periodEnd: usage.billingPeriod?.periodEnd || usage.periodEnd || null,
      periodDisplay: usage.periodDisplay || null,
    };
  } catch (e) {
    console.error('Error computing task time allowance:', e);
    return empty;
  }
}

export async function sumTaskTimeMetrics(accountId, { department } = {}) {
  const requests = await listTaskTimeRequests(accountId, { department });
  return { requests, ...computeTaskTimeMetrics(requests) };
}

// Legacy alias (Technology-era name); now returns pooled metrics across departments.
export async function sumTechnologyTaskTimeMetrics(accountId) {
  return sumTaskTimeMetrics(accountId);
}
